import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { getErrorMessage } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useStartOfWeek } from "../hooks/useStartOfWeek";
import { toast } from "./ui/Toast";
import { Button } from "./ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/Card";
import { LoadingOverlay } from "./ui/LoadingOverlay";
import { LoadingSpinner } from "./ui/LoadingSpinner";
import {
  BookOpen,
  Import,
  CheckCircle,
  CalendarDays,
  AlertCircle,
} from "lucide-react";
import { getWeekStartString, getDateRange } from "../utils/dateUtils";

export interface StarterCollectionMeal {
  name: string;
  description?: string;
  ingredients?: { name: string; quantity: number; unit: string }[];
  instructions?: string;
  servings: number;
  prep_time?: number;
  cook_time?: number;
  category?: string;
  dietary_tags?: string[];
}

export interface StarterCollection {
  id: number;
  name: string;
  description?: string;
  meals: StarterCollectionMeal[];
}

interface StarterCollectionsProps {
  onImportComplete?: (mealsImported: number) => void;
  compact?: boolean;
  planMode?: boolean;
  mealsUsed?: number;
}

const PLAN_MY_FIRST_WEEK_USED_KEY = "planMyFirstWeekUsed";

const StarterCollections: React.FC<StarterCollectionsProps> = ({
  onImportComplete,
  compact = false,
  planMode = false,
  mealsUsed = 0,
}) => {
  const { refreshLimits } = useAuth();
  const startOfWeek = useStartOfWeek();
  const navigate = useNavigate();
  const [collections, setCollections] = useState<StarterCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [importingId, setImportingId] = useState<number | null>(null);
  const [importedIds, setImportedIds] = useState<Set<number>>(new Set());
  const [planMyFirstWeekUsed, setPlanMyFirstWeekUsed] = useState(() => {
    try {
      return sessionStorage.getItem(PLAN_MY_FIRST_WEEK_USED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [progressStep, setProgressStep] = useState<
    "importing" | "planning" | null
  >(null);
  const [progressError, setProgressError] = useState<string | null>(null);

  const showPlanButton = planMode && mealsUsed <= 3 && !planMyFirstWeekUsed;

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await api.get("/starter-collections/");
        setCollections(response.data.items || []);
      } catch (error) {
        toast.error("Couldn't load starter collections", {
          description: getErrorMessage(error),
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, []);

  const handleImport = async (collectionId: number) => {
    setImportingId(collectionId);
    try {
      const response = await api.post(
        `/starter-collections/${collectionId}/import`,
      );
      const { meals_imported, meals_skipped } = response.data;
      setImportedIds((prev) => new Set(prev).add(collectionId));
      onImportComplete?.(meals_imported);
      await refreshLimits();
      toast.success("Collection imported", {
        description: `${meals_imported} meal${
          meals_imported !== 1 ? "s" : ""
        } added${
          meals_skipped > 0 ? `, ${meals_skipped} skipped (already exists)` : ""
        }.`,
      });
    } catch (error: any) {
      if (error.response?.status === 403) {
        toast.error("Import limit reached", {
          description: error.response?.data?.error?.detail?.upsell
            ? "Upgrade to premium for unlimited meals."
            : getErrorMessage(error, "Free tier meal limit reached."),
        });
      } else {
        toast.error("Couldn't import collection", {
          description: getErrorMessage(error),
        });
      }
    } finally {
      setImportingId(null);
    }
  };

  const handlePlanMyFirstWeek = async (collectionId: number) => {
    setImportingId(collectionId);
    setProgressStep("importing");
    setProgressError(null);

    // Step 1: Import the starter collection.
    let mealsImported = 0;
    let mealsSkipped = 0;
    try {
      const importResponse = await api.post(
        `/starter-collections/${collectionId}/import`,
      );
      mealsImported = importResponse.data.meals_imported;
      mealsSkipped = importResponse.data.meals_skipped;
      setImportedIds((prev) => new Set(prev).add(collectionId));
      onImportComplete?.(mealsImported);
      await refreshLimits();
    } catch (error: any) {
      setProgressError(
        error.response?.status === 403
          ? "Free tier meal limit reached. Upgrade to premium for unlimited meals."
          : getErrorMessage(error, "Failed to import recipes."),
      );
      setProgressStep(null);
      return;
    }

    // Step 2: Randomise the current week.
    setProgressStep("planning");
    let planningFailed = false;
    try {
      const randomizeResponse = await api.post("/meal-plans/randomize", {
        week_start: getWeekStartString(new Date(), startOfWeek),
        days: getDateRange(getWeekStartString(new Date(), startOfWeek)),
      });
      const { week_start } = randomizeResponse.data;
      setPlanMyFirstWeekUsed(true);
      try {
        sessionStorage.setItem(PLAN_MY_FIRST_WEEK_USED_KEY, "true");
      } catch {
        // Ignore storage errors in private browsing modes.
      }
      await refreshLimits();
      toast.success("Your first week is planned", {
        description: `${mealsImported} meal${
          mealsImported !== 1 ? "s" : ""
        } imported and your week is filled.${
          mealsSkipped > 0
            ? ` ${mealsSkipped} already existed and were skipped.`
            : ""
        }`,
      });
      navigate(`/meal-plans?week_start=${week_start.split("T")[0]}`);
    } catch (error: any) {
      planningFailed = true;
      setProgressError(getErrorMessage(error, "Failed to plan your week."));
      setProgressStep(null);
    }
    if (!planningFailed) {
      setImportingId(null);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-[200px]">
        <LoadingOverlay spinnerSize="md" text="Loading collections..." />
      </div>
    );
  }

  if (collections.length === 0) {
    return null;
  }

  return (
    <div className={compact ? "" : "py-12 bg-white"}>
      {showPlanButton && !compact && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <CalendarDays className="h-6 w-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-secondary-900">
              Plan Your First Week
            </h2>
          </div>
          <p className="text-secondary-600">
            Pick a starter collection and we will import the recipes and
            automatically fill your current week with meals.
          </p>
        </div>
      )}

      {!showPlanButton && !compact && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="h-6 w-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-secondary-900">
              Starter Recipe Collections
            </h2>
          </div>
          <p className="text-secondary-600">
            Import curated recipes to get your meal planning started quickly.
          </p>
        </div>
      )}

      <div
        className={
          compact
            ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
            : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        }
      >
        {collections.map((collection) => {
          const isImported = importedIds.has(collection.id);
          const isImporting = importingId === collection.id;

          return (
            <Card key={collection.id} className="flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary-100 p-2 rounded-lg">
                      <BookOpen className="h-5 w-5 text-primary-600" />
                    </div>
                    <CardTitle className="text-lg">{collection.name}</CardTitle>
                  </div>
                  {isImported && (
                    <CheckCircle className="h-5 w-5 text-success-500 flex-shrink-0" />
                  )}
                </div>
                {collection.description && (
                  <p className="text-sm text-secondary-600 mt-1">
                    {collection.description}
                  </p>
                )}
              </CardHeader>
              <CardContent className="flex-1 flex flex-col">
                <div className="flex-1">
                  <p className="text-sm text-secondary-500 mb-3">
                    {collection.meals.length} recipes
                  </p>
                  <ul className="space-y-1 mb-4">
                    {collection.meals.slice(0, 3).map((meal) => (
                      <li
                        key={meal.name}
                        className="text-sm text-secondary-700 flex items-center gap-2"
                      >
                        <span className="w-1 h-1 rounded-full bg-primary-400 flex-shrink-0" />
                        {meal.name}
                      </li>
                    ))}
                    {collection.meals.length > 3 && (
                      <li className="text-sm text-secondary-500 italic">
                        +{collection.meals.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
                <Button
                  onClick={() =>
                    showPlanButton
                      ? handlePlanMyFirstWeek(collection.id)
                      : handleImport(collection.id)
                  }
                  isLoading={isImporting && !showPlanButton}
                  disabled={
                    isImported ||
                    (isImporting && showPlanButton && !progressError)
                  }
                  variant={isImported ? "outline" : "primary"}
                  className="w-full"
                  aria-label={
                    showPlanButton
                      ? `Plan my first week with ${collection.name}`
                      : `Import ${collection.name}`
                  }
                >
                  {isImported ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Imported
                    </>
                  ) : showPlanButton ? (
                    <>
                      <CalendarDays className="mr-2 h-4 w-4" />
                      Plan My First Week
                    </>
                  ) : (
                    <>
                      <Import className="mr-2 h-4 w-4" />
                      Import Collection
                    </>
                  )}
                </Button>

                {/* Progress indicator for Plan My First Week flow */}
                {isImporting && showPlanButton && (
                  <div
                    className="mt-3 space-y-2"
                    role="status"
                    aria-live="polite"
                    data-testid="plan-first-week-progress"
                  >
                    {/* Step 1: Importing */}
                    <div className="flex items-center gap-2 text-sm">
                      {progressStep === "importing" ? (
                        <LoadingSpinner size="sm" />
                      ) : progressError ? (
                        <AlertCircle className="h-4 w-4 text-error-500 flex-shrink-0" />
                      ) : (
                        <CheckCircle className="h-4 w-4 text-success-500 flex-shrink-0" />
                      )}
                      <span
                        className={
                          progressError && progressStep === null
                            ? "text-error-600"
                            : "text-secondary-600"
                        }
                      >
                        {progressError && progressStep === null
                          ? "Import failed"
                          : progressStep === "importing"
                          ? "Importing recipes..."
                          : "Recipes imported"}
                      </span>
                    </div>

                    {/* Step 2: Planning */}
                    {!progressError && (
                      <div className="flex items-center gap-2 text-sm">
                        {progressStep === "planning" ? (
                          <LoadingSpinner size="sm" />
                        ) : progressStep === "importing" ? (
                          <div className="w-4 h-4 rounded-full border-2 border-secondary-200 flex-shrink-0" />
                        ) : (
                          <CheckCircle className="h-4 w-4 text-success-500 flex-shrink-0" />
                        )}
                        <span
                          className={
                            progressStep === "importing"
                              ? "text-secondary-400"
                              : "text-secondary-600"
                          }
                        >
                          {progressStep === "importing"
                            ? "Planning your week..."
                            : progressStep === "planning"
                            ? "Planning your week..."
                            : "Week planned!"}
                        </span>
                      </div>
                    )}

                    {/* Error message */}
                    {progressError && (
                      <p className="text-sm text-error-600 mt-1" role="alert">
                        {progressError}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default StarterCollections;
